import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth, useIsPlatformAdmin } from '../../../hooks/useAuth'
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs'
import { PageHeader } from '../../../components/shared/PageHeader'
import { SECTION_ICONS } from '../../../lib/section-icons'
import { Input } from '../../../components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../components/ui/table'
import { EmptyState } from '../../../components/ui/empty-state'
import { BillingAccountSection } from '../../../components/admin/BillingAccountSection'
import { BillingPricesSection } from '../../../components/admin/BillingPricesSection'
import { BillingIncludedSection } from '../../../components/admin/BillingIncludedSection'
import { BillingRecomputeSection } from '../../../components/admin/BillingRecomputeSection'
import { BillingInvoicesSection } from '../../../components/admin/BillingInvoicesSection'
import { BillingConsumptionSection } from '../../../components/admin/BillingConsumptionSection'
import { formatBillingValue, useAdminBillingHistory } from '../../../hooks/use-admin-billing'

interface HistorySearch {
  month?: string
}

export const Route = createFileRoute('/admin/billing/$orgUid')({
  validateSearch: (search: Record<string, unknown>): HistorySearch => {
    const out: HistorySearch = {}
    if (typeof search.month === 'string' && /^\d{4}-\d{2}$/.test(search.month)) out.month = search.month
    return out
  },
  component: RouteComponent,
})

function currentMonth(): string {
  return new Date().toISOString().slice(0, 7)
}

/** monthRange returns the first and last day (YYYY-MM-DD) of a YYYY-MM month. */
function monthRange(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number)
  const last = new Date(Date.UTC(y, m, 0)).getUTCDate()
  return { from: `${month}-01`, to: `${month}-${String(last).padStart(2, '0')}` }
}

function RouteComponent() {
  const { t, i18n } = useTranslation('admin')
  const { orgUid } = Route.useParams()
  const search = Route.useSearch()
  const { loading, orgCode } = useAuth()
  const isPlatformAdmin = useIsPlatformAdmin()
  const navigate = useNavigate({ from: '/admin/billing/$orgUid' })

  useEffect(() => {
    if (!loading && !isPlatformAdmin) {
      navigate({ to: '/', replace: true })
    }
  }, [loading, isPlatformAdmin, navigate])

  const month = search.month ?? currentMonth()
  const { from, to } = monthRange(month)
  const { data, isLoading, error } = useAdminBillingHistory(orgUid, from, to)
  const metrics = data?.metrics ?? []
  const rows = data?.data ?? []
  const lang = i18n.language.startsWith('fr') ? 'fr' : 'en'

  const breadcrumbOrg = orgCode || 'demo'
  useBreadcrumbs([
    { title: t('common:nav.home'), path: `/${breadcrumbOrg}` },
    { title: t('settings:page.title'), path: `/${breadcrumbOrg}/settings` },
    { title: t('billing.title'), path: '/admin/billing' },
    { title: data?.organization_name || data?.organization_code || orgUid },
  ])

  if (loading || !isPlatformAdmin) {
    return null
  }

  return (
    <div className="space-y-6" data-testid="admin-billing-history-page">
      <PageHeader
        icon={SECTION_ICONS.adminBilling}
        title={data?.organization_name || data?.organization_code || t('billing.title')}
        description={t('billing.history_subtitle')}
      />

      <BillingAccountSection orgUid={orgUid} />
      <BillingPricesSection orgUid={orgUid} />
      <BillingIncludedSection orgUid={orgUid} />
      <BillingConsumptionSection orgUid={orgUid} />
      <BillingInvoicesSection orgUid={orgUid} />

      <BillingRecomputeSection orgUid={orgUid} />

      <h2 className="text-lg font-semibold">{t('billing.history_title')}</h2>
      <div className="bg-card rounded-lg border border-border p-4">
        <Input
          type="month"
          value={month}
          onChange={(e) => {
            if (e.target.value) navigate({ search: { month: e.target.value }, replace: true })
          }}
          className="w-44"
          aria-label={t('billing.month')}
          data-testid="billing-month"
        />
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-md p-4 text-destructive text-sm" data-testid="billing-error">
          {t('billing.load_error')}
        </div>
      )}

      <div className="bg-card rounded-lg border border-border overflow-hidden">
        {isLoading ? (
          <div className="px-6 py-4 text-center text-muted-foreground">{t('billing.loading')}</div>
        ) : rows.length === 0 ? (
          <EmptyState variant="inline" live className="py-8" title={t('billing.history_empty')} data-testid="billing-history-empty" />
        ) : (
          <Table data-testid="billing-history-table">
            <TableHeader>
              <TableRow>
                <TableHead>{t('billing.column.day')}</TableHead>
                {metrics.map((m) => (
                  <TableHead key={m.code} className="text-right">{m.label[lang] ?? m.label.en}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row.day} data-testid={`billing-history-row-${row.day}`}>
                  <TableCell>{row.day}</TableCell>
                  {metrics.map((m) => (
                    <TableCell key={m.code} className="text-right tabular-nums" data-testid={`billing-history-${row.day}-${m.code}`}>
                      {formatBillingValue(row.metrics[m.code], m.unit, i18n.language)}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  )
}
